import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { buildUpcomingDays, toISODate, mondayIndex, WEEKDAYS_FR, MONTHS_FR, formatDateFR } from "@/lib/dates";
import { X, Phone, Mail, Car, Calendar as CalIcon, Clock, Loader2, CheckCircle2, Ban, RefreshCw, Euro, User } from "lucide-react";
import { toast } from "sonner";

const statusMap = {
  confirmed: { label: "Confirmée", cls: "bg-[#06D6A0]/15 text-[#06D6A0] border-[#06D6A0]/30" },
  done: { label: "Terminée", cls: "bg-[#00B4D8]/15 text-[#00B4D8] border-[#00B4D8]/30" },
  cancelled: { label: "Annulée", cls: "bg-[#EF476F]/15 text-[#EF476F] border-[#EF476F]/30" },
};
const inputCls = "bg-[#050810] border border-[#1A2642] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]";

export default function BookingDetailModal({ booking, onClose, onChanged }) {
  const [b, setB] = useState(booking);
  const [amount, setAmount] = useState(booking?.amount_paid != null ? String(booking.amount_paid) : "");
  const [busy, setBusy] = useState(false);
  const [resch, setResch] = useState(false);
  const [days] = useState(() => buildUpcomingDays(28).slice(0, 14));
  const [selDate, setSelDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selSlot, setSelSlot] = useState(null);
  const [slotLoading, setSlotLoading] = useState(false);

  useEffect(() => { setB(booking); setAmount(booking?.amount_paid != null ? String(booking.amount_paid) : ""); setResch(false); setSelDate(null); setSelSlot(null); }, [booking]);
  if (!b) return null;

  const refresh = (updated) => { setB(updated); onChanged?.(updated); };

  const setStatus = async (status, withAmount = false) => {
    setBusy(true);
    try {
      const payload = { status };
      if (withAmount && amount !== "") payload.amount_paid = parseFloat(amount);
      const { data } = await api.put(`/admin/bookings/${b.id}`, payload);
      refresh(data);
      toast.success(status === "done" ? "RDV clôturé" : status === "cancelled" ? "RDV annulé" : "Statut mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveAmount = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/bookings/${b.id}`, { amount_paid: amount === "" ? 0 : parseFloat(amount) });
      refresh(data);
      toast.success("Montant enregistré");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const loadSlots = async (iso) => {
    setSelDate(iso); setSelSlot(null); setSlots([]); setSlotLoading(true);
    try {
      const { data } = await api.get(`/availability`, { params: { service_id: b.service_id, date: iso } });
      setSlots(data.slots);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSlotLoading(false); }
  };

  const confirmReschedule = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/reschedule`, { date: selDate, start_time: selSlot });
      refresh(data); setResch(false);
      toast.success("RDV décalé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Row = ({ icon: Icon, children }) => (
    <div className="flex items-center gap-2.5 text-sm text-[#CBD5E1]"><Icon className="w-4 h-4 text-[#00B4D8] shrink-0" />{children}</div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="booking-detail-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto glass border border-[#1A2642] rounded-2xl animate-float-up">
        {/* header */}
        <div className="sticky top-0 glass border-b border-[#1A2642] px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${statusMap[b.status].cls}`} data-testid="detail-status">{statusMap[b.status].label}</span>
              {b.amount_paid != null && b.amount_paid > 0 && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-[#06D6A0]/30 text-[#06D6A0]">Payé {b.amount_paid} €</span>}
            </div>
            <h3 className="font-heading text-xl font-bold text-white mt-1">{b.service_name}</h3>
          </div>
          <button onClick={onClose} data-testid="detail-close" className="text-[#94A3B8] hover:text-white transition shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* date/time + price */}
          <div className="flex items-center justify-between bg-[#050810] border border-[#1A2642] rounded-lg p-3">
            <div className="space-y-1.5">
              <Row icon={CalIcon}>{formatDateFR(b.date)}</Row>
              <Row icon={Clock}>{b.start_time} → {b.end_time}</Row>
            </div>
            <span className="font-heading text-2xl font-black text-[#FFD60A]">{b.service_price} €</span>
          </div>

          {/* client */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00B4D8] mb-2">Client</p>
            <div className="space-y-2">
              <Row icon={User}>{b.customer_name}</Row>
              <a href={`tel:${b.customer_phone}`} className="block hover:text-white"><Row icon={Phone}>{b.customer_phone}</Row></a>
              <a href={`mailto:${b.customer_email}`} className="block hover:text-white"><Row icon={Mail}>{b.customer_email}</Row></a>
              <Row icon={Car}>{b.vehicle_marque} {b.vehicle_modele} ({b.vehicle_annee}){b.vehicle_immatriculation ? ` · ${b.vehicle_immatriculation}` : ""}</Row>
              {b.notes && <p className="text-sm text-[#94A3B8] italic pl-6">« {b.notes} »</p>}
            </div>
          </div>

          {/* amount paid */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00B4D8] mb-2">Montant payé (optionnel)</p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <input type="number" min="0" step="1" className={`${inputCls} w-full pl-9`} data-testid="detail-amount" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <button onClick={saveAmount} disabled={busy} data-testid="detail-save-amount" className="border border-[#1A2642] text-white text-sm font-semibold rounded-full px-4 py-2 hover:border-[#00B4D8] disabled:opacity-50 transition">Enregistrer</button>
            </div>
          </div>

          {/* status changer */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00B4D8] mb-2">Statut</p>
            <div className="flex gap-2">
              {["confirmed", "done", "cancelled"].map((s) => (
                <button key={s} onClick={() => setStatus(s)} disabled={busy} data-testid={`detail-set-${s}`}
                  className={`flex-1 text-xs font-bold rounded-full py-2 border transition ${b.status === s ? statusMap[s].cls : "border-[#1A2642] text-[#94A3B8] hover:border-[#00B4D8]"}`}>
                  {statusMap[s].label}
                </button>
              ))}
            </div>
          </div>

          {/* reschedule panel */}
          {resch && (
            <div className="bg-[#050810] border border-[#1A2642] rounded-lg p-3" data-testid="detail-reschedule-panel">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#94A3B8] mb-2">Nouvelle date</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {days.map((d) => {
                  const iso = toISODate(d); const active = selDate === iso;
                  return (
                    <button key={iso} onClick={() => loadSlots(iso)} data-testid={`detail-day-${iso}`}
                      className={`shrink-0 w-14 py-2 rounded-md border text-center text-xs ${active ? "bg-[#00B4D8] border-[#00B4D8] text-[#050810]" : "border-[#1A2642] hover:border-[#00B4D8]"}`}>
                      <div className="font-bold">{WEEKDAYS_FR[mondayIndex(d)]}</div>
                      <div className="text-lg font-bold">{d.getDate()}</div>
                      <div>{MONTHS_FR[d.getMonth()]}</div>
                    </button>
                  );
                })}
              </div>
              {selDate && (slotLoading ? <Loader2 className="w-5 h-5 animate-spin my-3 text-[#00B4D8]" /> : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-2">
                  {slots.length === 0 ? <p className="text-sm text-[#94A3B8] col-span-full">Aucun créneau ce jour.</p> :
                    slots.map((s) => (
                      <button key={s} onClick={() => setSelSlot(s)} data-testid={`detail-slot-${s}`}
                        className={`py-2 rounded-md border text-sm font-semibold ${selSlot === s ? "bg-[#FFD60A] border-[#FFD60A] text-[#050810]" : "border-[#1A2642] hover:border-[#00B4D8]"}`}>{s}</button>
                    ))}
                </div>
              ))}
              <div className="flex gap-2 mt-3">
                <button disabled={!selSlot || busy} onClick={confirmReschedule} data-testid="detail-confirm-reschedule" className="bg-[#00B4D8] text-[#050810] font-bold rounded-full px-5 py-2.5 text-sm disabled:opacity-40 hover:bg-[#90E0EF] transition flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Valider</button>
                <button onClick={() => setResch(false)} className="border border-[#1A2642] rounded-full px-5 py-2.5 text-sm hover:border-white transition">Annuler</button>
              </div>
            </div>
          )}

          {/* actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#1A2642]">
            <button onClick={() => setStatus("done", true)} disabled={busy} data-testid="detail-close-rdv" className="flex items-center gap-1.5 bg-[#FFD60A] text-[#050810] font-bold text-sm rounded-full px-4 py-2.5 hover:bg-[#FFEA00] disabled:opacity-50 transition"><CheckCircle2 className="w-4 h-4" /> Clôturer le RDV</button>
            <button onClick={() => setResch((r) => !r)} disabled={busy} data-testid="detail-reschedule-btn" className="flex items-center gap-1.5 border border-[#1A2642] text-white font-semibold text-sm rounded-full px-4 py-2.5 hover:border-[#00B4D8] transition"><RefreshCw className="w-4 h-4" /> Décaler</button>
            <button onClick={() => setStatus("cancelled")} disabled={busy} data-testid="detail-cancel-rdv" className="flex items-center gap-1.5 text-[#EF476F] border border-[#EF476F]/30 font-semibold text-sm rounded-full px-4 py-2.5 hover:bg-[#EF476F]/10 transition"><Ban className="w-4 h-4" /> Annuler</button>
          </div>
        </div>
      </div>
    </div>
  );
}
